<script setup>
/* eslint-disable no-use-before-define */
import { ref, computed, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const showAlert = useAlert;

const inboxName = ref('');
const phoneNumber = ref('');
const isCreating = ref(false);
const inboxId = ref(null);
const qrCode = ref(null);
const connected = ref(false);
const qrError = ref(false);
const pollingTimer = ref(null);

const accountId = computed(() => route.params.accountId);

const accessToken = computed(() => store.getters.getCurrentUser?.access_token);

const authHeaders = computed(() => ({
  'Content-Type': 'application/json',
  api_access_token: accessToken.value,
}));

const phoneError = computed(() => {
  if (!phoneNumber.value)
    return t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_API.PHONE_NUMBER.ERROR');
  const cleaned = phoneNumber.value.replace(/\D/g, '');
  if (cleaned.length < 10)
    return t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_API.PHONE_NUMBER.ERROR');
  return '';
});

const canSubmit = computed(
  () =>
    inboxName.value.trim().length > 0 &&
    phoneNumber.value.replace(/\D/g, '').length >= 10 &&
    !isCreating.value
);

async function createInbox() {
  if (!canSubmit.value) return;
  isCreating.value = true;
  qrError.value = false;

  try {
    const res = await fetch(
      `/api/v1/accounts/${accountId.value}/evolution_api/inboxes`,
      {
        method: 'POST',
        headers: authHeaders.value,
        body: JSON.stringify({
          inbox: {
            name: inboxName.value.trim(),
            phone_number: phoneNumber.value.replace(/\D/g, ''),
          },
        }),
      }
    );

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'Erro ao criar inbox');
    }

    inboxId.value = data.id;
    await fetchQrCode();
    startPolling();
  } catch (err) {
    showAlert(err?.message || 'Erro ao criar inbox. Tente novamente.');
    isCreating.value = false;
  }
}

async function fetchQrCode() {
  if (!inboxId.value) return;
  try {
    const res = await fetch(
      `/api/v1/accounts/${accountId.value}/evolution_api/inboxes/${inboxId.value}/qrcode`,
      { headers: authHeaders.value }
    );
    const data = await res.json();
    if (data.qr_code) {
      qrCode.value = data.qr_code.startsWith('data:')
        ? data.qr_code
        : `data:image/png;base64,${data.qr_code}`;
      qrError.value = false;
    } else {
      qrError.value = true;
    }
  } catch {
    qrError.value = true;
  }
}

async function checkStatus() {
  if (!inboxId.value) return;
  try {
    const res = await fetch(
      `/api/v1/accounts/${accountId.value}/evolution_api/inboxes/${inboxId.value}/status`,
      { headers: authHeaders.value }
    );
    const data = await res.json();
    if (['open', 'connected'].includes(data.status?.toLowerCase())) {
      connected.value = true;
      stopPolling();
      setTimeout(() => {
        router.push({
          name: 'settings-inboxes-add-agents',
          params: { inbox_id: inboxId.value },
        });
      }, 2000);
    } else if (data.status === 'close' || data.status === 'qr_code') {
      fetchQrCode();
    }
  } catch {
    /* silently ignore */
  }
}

function startPolling() {
  pollingTimer.value = setInterval(() => checkStatus(), 4000);
}

function stopPolling() {
  if (pollingTimer.value) {
    clearInterval(pollingTimer.value);
    pollingTimer.value = null;
  }
}

onUnmounted(() => stopPolling());
</script>

<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<template>
  <div class="flex flex-col gap-6">
    <!-- Form: nome + número -->
    <div v-if="!inboxId" class="flex flex-col gap-4">
      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_API.INBOX_NAME.LABEL') }}
        </label>
        <input
          v-model="inboxName"
          type="text"
          class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_API.INBOX_NAME.PLACEHOLDER')
          "
        />
      </div>

      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_API.PHONE_NUMBER.LABEL') }}
        </label>
        <input
          v-model="phoneNumber"
          type="text"
          class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-alpha-1 text-n-slate-12 focus:outline-none focus:border-n-brand"
          :placeholder="
            $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_API.PHONE_NUMBER.PLACEHOLDER')
          "
        />
        <p v-if="phoneNumber && phoneError" class="mt-1 text-xs text-r-400">
          {{ phoneError }}
        </p>
      </div>

      <button
        :disabled="!canSubmit"
        class="flex justify-center items-center px-4 py-2 rounded-lg bg-n-brand text-white font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-n-brand/90 transition-colors"
        @click="createInbox"
      >
        <span v-if="isCreating" class="animate-spin mr-2">⟳</span>
        {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_API.SUBMIT_BTN') }}
      </button>
    </div>

    <!-- QR Code -->
    <div v-else class="flex flex-col items-center gap-4 py-4">
      <div
        v-if="connected"
        class="flex flex-col items-center gap-3 text-center"
      >
        <div
          class="w-16 h-16 rounded-full bg-g-50 flex items-center justify-center"
        >
          <span class="text-3xl">✓</span>
        </div>
        <p class="text-sm font-medium text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_API.QR_CODE.CONNECTED') }}
        </p>
      </div>

      <template v-else>
        <h3 class="text-base font-semibold text-n-slate-12">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_API.QR_CODE.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 text-center max-w-sm">
          {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_API.QR_CODE.INSTRUCTION') }}
        </p>

        <div
          class="w-56 h-56 border-2 border-n-weak rounded-xl flex items-center justify-center overflow-hidden"
        >
          <img
            v-if="qrCode && !qrError"
            :src="qrCode"
            alt="QR Code"
            class="w-full h-full object-contain"
          />
          <div
            v-else-if="qrError"
            class="flex flex-col items-center gap-2 p-4 text-center"
          >
            <p class="text-xs text-n-slate-11">
              {{ $t('INBOX_MGMT.ADD.WHATSAPP.EVOLUTION_API.QR_CODE.ERROR') }}
            </p>
            <button class="text-xs text-n-brand underline" @click="fetchQrCode">
              Tentar novamente
            </button>
          </div>
          <div v-else class="flex items-center justify-center">
            <span class="animate-spin text-2xl text-n-brand">⟳</span>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
